'use client';
import type React from 'react';
import { memo } from 'react';
import { useEffect, useState } from 'react';
import 'leaflet/dist/leaflet.css';

import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.webpack.css';

import 'leaflet-defaulticon-compatibility';
import { MapContainer, Marker, Popup } from 'react-leaflet';
import './map.css';
import type { RouteHookProps } from '@dolcevia/lib/hooks/useRoute';
import {
  coordinatesParser,
  stepsSelectionSchemaParser,
  travelModeParamsParser,
} from '@dolcevia/lib/searchParams';
import { cn } from '@dolcevia/lib/utils';
import type { LatLngExpression, Map as MapType } from 'leaflet';
import { useQueryStates } from 'nuqs';
import VectorTileLayer from 'react-leaflet-vector-tile-layer';
import { iconDolcevia } from './assets/marker';
import { ActivitiesMarker } from './components/activities-marker';
import {
  ControlToggle,
  type DisplayedMarkers,
} from './components/control-toggle';
import {
  ICON_MARKER_MAP,
  StepSelectionMarker,
} from './components/step-selection-marker';
import { VillagesMarker } from './components/villages-marker';
import { useDraggableMarker } from './map.hooks';
import { useMapPointOfInterests } from './useMapPointOfInterests';
const DEFAULT_POSITION = { lng: 2.3479552268981934, lat: 48.8685188293457 };

export interface MapProps {
  button?: React.ReactNode;
  displayMap?: boolean;
  centerOverride?: { lat: number; lng: number };
  zoomOverride?: number;
  className?: string;
  additionalMarkers?: Array<{
    lat: number;
    lng: number;
    popupContent?: React.ReactNode;
    type: 'booking' | 'tripadvisor' | 'greengo' | 'airbnb' | 'activity';
  }>;
  routeOverride?: RouteHookProps;
  redirectUrl?: string;
  hideExtraMarkers?: boolean;
}

const LOCAL_STORAGE_KEY = 'planner-map-pref';

const MapContent = ({
  button,
  displayMap,
  className,
  additionalMarkers,
  routeOverride,
  redirectUrl,
  centerOverride,
  zoomOverride,
  hideExtraMarkers = false,
}: MapProps) => {
  const [_map, setMap] = useState<MapType | null>(null);
  const [{ travelMode, coordinates, stepsSelection }] = useQueryStates({
    travelMode: travelModeParamsParser,
    coordinates: coordinatesParser,
    stepsSelection: stepsSelectionSchemaParser,
  });
  const params = {
    coordinates: routeOverride?.coordinatesOverride ?? coordinates,
    stepsSelection: routeOverride?.stepsSelectionOverride ?? stepsSelection,
  };

  const [displayedMarkers, setDisplayedMarkers] = useState<DisplayedMarkers>(
    () => {
      if (localStorage.getItem(LOCAL_STORAGE_KEY) === null)
        return { museums: true, villages: true, activities: true };

      try {
        return JSON.parse(
          localStorage.getItem(LOCAL_STORAGE_KEY) ??
            '{"museums": true, "villages": true, "activities": true}',
        );
      } catch {
        return {};
      }
    },
  );
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(displayedMarkers));
  }, [displayedMarkers]);
  const { data: villages, activities } = useMapPointOfInterests();

  useDraggableMarker(_map, routeOverride);

  return (
    <div
      className={cn(className)}
      key={coordinates.map((c) => (c.lat ?? 0) + (c.lng ?? 0)).join()}
    >
      <div
        className={cn(
          'w-full rounded-lg ',
          !button && 'h-full min-h-[450px]',
          displayMap && button && 'h-[450px]',
          !displayMap && button && 'hidden',
        )}
      >
        <MapContainer
          key={travelMode.mode}
          ref={setMap}
          center={
            centerOverride ??
            ((params.coordinates &&
            params.coordinates?.length > 0 &&
            params.coordinates[0].lat &&
            params.coordinates[0].lng &&
            params.coordinates.every((el) => el?.lat && el?.lng)
              ? {
                  lat: params.coordinates[0].lat,
                  lng: params.coordinates[0].lng,
                }
              : DEFAULT_POSITION) as LatLngExpression)
          }
          bounds={
            params.coordinates?.every((el) => el?.lat && el?.lng)
              ? params.coordinates?.map((el) => [el.lat ?? 0, el.lng ?? 0])
              : undefined
          }
          zoom={
            zoomOverride ??
            (params.coordinates.every((el) => el?.lat && el?.lng)
              ? undefined
              : 5)
          }
          maxZoom={15}
          scrollWheelZoom={true}
          className={cn('w-full rounded-lg', !button && 'h-full')}
          style={{ height: button ? 450 : '100%' }}
        >
          <VectorTileLayer styleUrl="https://api.maptiler.com/maps/d8aab919-f30f-46e8-88fc-fbc18d5d51ba/style.json?key=9rrA9YZyQyICWP9U56vR" />
          <ControlToggle
            redirectUrl={redirectUrl}
            displayedMarkers={displayedMarkers}
            setDisplayedMarkers={setDisplayedMarkers}
          />
          {params.coordinates?.map(
            (coordinate) =>
              coordinate.lat &&
              coordinate.lng && (
                <Marker
                  icon={iconDolcevia}
                  key={coordinate.lat + coordinate.lng}
                  position={[coordinate.lat, coordinate.lng]}
                />
              ),
          )}
          <StepSelectionMarker stepsSelection={params.stepsSelection} />

          {additionalMarkers?.map((marker, index) => (
            <Marker
              icon={ICON_MARKER_MAP[marker.type]}
              key={marker.type + marker.lat + marker.lng + index}
              position={[marker.lat, marker.lng]}
            >
              {marker.popupContent && <Popup>{marker.popupContent}</Popup>}
            </Marker>
          ))}
          <ActivitiesMarker
            activities={activities}
            isActivitiesDisplayed={
              displayedMarkers.activities && !hideExtraMarkers
            }
          />
          <VillagesMarker
            villages={villages}
            isVillageDisplayed={displayedMarkers.villages && !hideExtraMarkers}
          />
        </MapContainer>
      </div>
      {button}
    </div>
  );
};
const CustomMap = memo(MapContent);

export default CustomMap;
